import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Play, Download, Check, Volume2, VolumeX, RotateCcw } from 'lucide-react';
import { trustMarkers } from '../mock';

const Hero = ({ onBookingClick }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const videoRef = useRef(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const handleVideoEnd = () => {
    setVideoEnded(true);
  };

  const handleWatchAgain = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setVideoEnded(false);
    }
  };

  const handleDownloadRider = () => {
    alert('Tech rider download will be implemented in backend');
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-8 pb-16 px-4 sm:px-6 lg:px-8">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-transparent opacity-90"></div>
      
      <div className="relative z-10 container mx-auto text-center">
        {/* Logo */}
        <div className="mb-0">
          <img
            src="https://customer-assets.emergentagent.com/job_elvis-one-more-time/artifacts/xbikd373_Logo.png"
            alt="Elvis One More Time Logo"
            className="mx-auto w-auto h-80 sm:h-96 lg:h-[28rem] object-contain"
          />
        </div>

        {/* Tagline */}
        <p className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-gold/90 font-light italic mb-4 whitespace-nowrap -mt-8 sm:-mt-10 lg:-mt-12">
          "re-live the memory...One More Time!"
        </p>

        {/* Social Media Icons */}
        <div className="flex justify-center gap-6 mb-4">
          <a
            href="https://www.facebook.com/elvis1moretime"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform hover:scale-110"
            aria-label="Facebook"
            data-testid="social-facebook"
          >
            <svg className="w-10 h-10" viewBox="0 0 24 24">
              <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <a
            href="https://www.instagram.com/elvis1moretime"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform hover:scale-110"
            aria-label="Instagram"
            data-testid="social-instagram"
          >
            <svg className="w-10 h-10" viewBox="0 0 24 24">
              <defs>
                <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FFDC80"/>
                  <stop offset="25%" stopColor="#FCAF45"/>
                  <stop offset="50%" stopColor="#F77737"/>
                  <stop offset="75%" stopColor="#F56040"/>
                  <stop offset="87.5%" stopColor="#E1306C"/>
                  <stop offset="100%" stopColor="#833AB4"/>
                </linearGradient>
              </defs>
              <path fill="url(#instagram-gradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
        </div>

        {/* Video Showreel Module */}
        <div className="mb-4 max-w-4xl mx-auto">
          <div className="relative aspect-video bg-black rounded-xl overflow-hidden border-2 border-gold/30 hover:border-gold group gold-glow transition-all duration-300">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              className="absolute inset-0 w-full h-full object-contain bg-black"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_49ea4133-d43d-4d62-a0e3-b1ab9a95136b/artifacts/ih228wa8_Gary%20Jay%20Showreel.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
            
            {/* Watch Again Overlay */}
            {videoEnded && (
              <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                <button
                  onClick={handleWatchAgain}
                  className="flex flex-col items-center gap-3 text-gold hover:text-white transition-colors"
                  data-testid="watch-again-btn"
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-gold hover:border-white flex items-center justify-center transition-colors">
                    <RotateCcw size={32} className="sm:w-10 sm:h-10" />
                  </div>
                  <span className="text-lg sm:text-xl font-semibold">Watch Again</span>
                </button>
              </div>
            )}
            
            {/* Custom Unmute Button - Always Visible */}
            {!videoEnded && (
              <button
                onClick={toggleMute}
                className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 flex items-center gap-1 sm:gap-2 bg-black/80 hover:bg-gold text-white hover:text-black px-2 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-all duration-300 border border-gold/50 hover:border-gold shadow-lg"
                aria-label={isMuted ? "Unmute video" : "Mute video"}
              >
                {isMuted ? (
                  <>
                    <VolumeX size={16} className="sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-medium">Sound</span>
                  </>
                ) : (
                  <>
                    <Volume2 size={16} className="sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-medium">Sound On</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* CTAs */}
        <div className="flex justify-center items-center mb-12">
          <Button
            onClick={onBookingClick}
            className="bg-gold text-black hover:bg-gold/90 font-bold text-lg px-8 py-6 rounded-lg gold-glow-strong"
          >
            Check Availability & Book
          </Button>
        </div>

        {/* Trust Strip */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-silver">
          {trustMarkers.map((marker, index) => (
            <div key={index} className="flex items-center gap-2">
              <Check className="text-gold" size={20} />
              <span className="text-sm lg:text-base font-medium">{marker}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
